<template>
  <div class="chart">
    <ChartWrapper
      ref="chartWrapper"
      :actions="actions"
      :chart-resolution-from-dashboard="chartResolutionFromDashboard"
      :chart-warnings="chartWarnings"
      :dont-trim-data-from-chart="dontTrimDataFromChart"
      :echarts-options="chartsOptions"
      :highlight-current-period="highlightCurrentPeriod"
      :is-fullscreen="false"
      :is-impressions-ad-spend-chart="isImpressionsAdSpendChart"
      :margin-top="marginTop"
      :mode="mode"
      :name="name"
      :should-daily-cap-toggle-be-available="shouldDailyCapToggleBeAvailable"
      :should-show-daily-cap="shouldShowDailyCap"
      :show-future="showFuture"
      :template-ref="templateRef"
      :theme="theme"
      @show-daily-cap="showDailyCap"
      @go-fullscreen="goFullscreen"
      @timezone-selected="timezoneSelected"
      @update-chart-type="updateChartType"
      @update-chart-granularity="updateChartGranularity" />

    <modal
      :visible="isFullscreen"
      :title="name"
      :use-full-width="true"
      :use-full-height="true"
      :no-padding="true"
      @confirmed="() => (isFullscreen = false)"
      @cancelled="() => (isFullscreen = false)">
      <template #modal-after-form>
        <chart-wrapper
          ref="chartWrapper"
          :actions="actions"
          :echarts-options="chartsOptions"
          :dont-trim-data-from-chart="dontTrimDataFromChart"
          :chart-warnings="chartWarnings"
          :chart-resolution-from-dashboard="chartResolutionFromDashboard"
          :should-show-daily-cap="shouldShowDailyCap"
          :should-daily-cap-toggle-be-available="shouldDailyCapToggleBeAvailable"
          :highlight-current-period="highlightCurrentPeriod"
          :is-fullscreen="true"
          :is-impressions-ad-spend-chart="isImpressionsAdSpendChart"
          :margin-top="marginTop"
          :mode="chartModes.VERBOSE"
          :name="name"
          :show-future="showFuture"
          :show-table="true"
          :template-ref="`${templateRef}.fullscreen`"
          :theme="theme"
          @show-daily-cap="showDailyCap"
          @timezone-selected="timezoneSelected"
          @update-chart-type="updateChartType"
          @update-chart-granularity="updateChartGranularity" />
      </template>
    </modal>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, useTemplateRef, watch } from 'vue';

  import ChartWrapper from '@/common/components/chart/ChartWrapper.vue';
  import Modal from '@/common/components/controls/Modal.vue';
  import ChartEvents from '@/common/enums/chart-events.enum';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartGroupAction from '@/common/models/chart/ChartGroupAction';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { waitUntil } from '@/utils/DelayHelper';

  import { IChartWarning } from './IChartWarning';
  import IShouldDailyCapToggleBeAvailable from './IShouldDailyCapToggleBeAvailable';

  interface IProps {
    actions?: ChartGroupAction[];
    dontTrimDataFromChart?: boolean;
    chartWarnings?: Record<string, IChartWarning>;
    shouldShowDailyCap?: boolean;
    shouldDailyCapToggleBeAvailable?: IShouldDailyCapToggleBeAvailable;
    echartsOptions: any;
    chartResolutionFromDashboard?: string;
    highlightCurrentPeriod?: boolean;
    mode: string;
    name: string;
    showFuture?: boolean;
    templateRef: string;
    theme: string;
    isImpressionsAdSpendChart?: boolean;
    marginTop?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    actions: null,
    dontTrimDataFromChart: false,
    chartWarnings: null,
    shouldShowDailyCap: true,
    shouldDailyCapToggleBeAvailable: null,
    echartsOptions: null,
    chartResolutionFromDashboard: null,
    highlightCurrentPeriod: false,
    mode: ChartMode.NONE,
    name: null,
    showFuture: false,
    templateRef: null,
    theme: null,
    isImpressionsAdSpendChart: false,
    marginTop: '-4.5rem',
  });

  const emit = defineEmits([
    ChartEvents.SHOW_DAILY_CAP,
    'go-fullscreen',
    ChartEvents.TIMEZONE_SELECTED,
    ChartEvents.CHART_TYPE_UPDATED,
    ChartEvents.CHART_GRANULARITY_UPDATED,
  ]);

  const chartModes = ChartMode;
  const isFullscreen = ref(false);
  const chartWrapper = useTemplateRef<typeof ChartWrapper>('chartWrapper');
  const granularity = ref(props.echartsOptions?.xAxis?.granularity);

  const chartsOptions = computed(() => {
    if (!props.echartsOptions) {
      return {};
    }
    const output = { ...props.echartsOptions };
    if (props.echartsOptions.xAxis != null) {
      output.xAxis = { ...props.echartsOptions.xAxis, granularity: granularity.value }; // Insert cached granularity
    }
    return output;
  });

  watch(isFullscreen, (val) => {
    GlobalEventBus.$emit(GlobalEvents.toggleSaveAndCancel, val);
  });

  watch(
    () => props.echartsOptions,
    (val) => {
      if (val?.xAxis?.granularity != granularity.value) {
        granularity.value = val.xAxis?.granularity;
      }
    },
    {
      deep: true,
    },
  );

  const goFullscreen = () => {
    isFullscreen.value = true;
  };

  const timezoneSelected = (option: any) => {
    emit(ChartEvents.TIMEZONE_SELECTED, option);
  };

  const updateChartType = (type: any) => {
    emit(ChartEvents.CHART_TYPE_UPDATED, type);
  };

  const updateChartGranularity = (g: any) => {
    granularity.value = g;
    emit(ChartEvents.CHART_GRANULARITY_UPDATED, g);
  };

  const showDailyCap = () => {
    emit(ChartEvents.SHOW_DAILY_CAP);
  };

  async function updateChartInterval(action, interval) {
    await waitUntil(() => chartWrapper?.value?.intervalSelection != null);
    if (action && interval) {
      chartWrapper.value.intervalSelection(action, interval);
    }
  }
  defineExpose({ updateChartInterval });
</script>

<style lang="scss" scoped>
  .chart {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    position: relative;

    .modal .modal-dialog .modal-body {
      max-height: 90vh !important;
    }
  }
</style>
